import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Phone, Clock, Star, ArrowLeft, Globe, Calendar, ExternalLink, FileText, ChevronRight, Loader2, Store } from 'lucide-react';
import { getRestaurant, getRestaurantOffers, getNearbyRestaurants, getSimilarCuisineRestaurants, getRestaurantGoogleDetails } from '../services/api';
import DealCard from '../components/DealCard';
import HygieneBadge from '../components/HygieneBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RestaurantDetailPage = () => {
  const { id } = useParams();
  const [restaurant, setRestaurant] = useState(null);
  const [offers, setOffers] = useState([]);
  const [nearbyRestaurants, setNearbyRestaurants] = useState([]);
  const [similarRestaurants, setSimilarRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [googleInfo, setGoogleInfo] = useState(null);
  const [loadingGoogle, setLoadingGoogle] = useState(false);

  useEffect(() => {
    let active = true;
    setGoogleInfo(null);
    setLoadingGoogle(true);
    getRestaurantGoogleDetails(id)
      .then((res) => { if (active) setGoogleInfo(res.data); })
      .catch(() => { if (active) setGoogleInfo({ available: false }); })
      .finally(() => { if (active) setLoadingGoogle(false); });
    return () => { active = false; };
  }, [id]);

  const defaultImages = [
    'https://images.pexels.com/photos/6205791/pexels-photo-6205791.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [restaurantRes, offersRes] = await Promise.all([
          getRestaurant(id),
          getRestaurantOffers(id)
        ]);
        setRestaurant(restaurantRes.data);
        setOffers(offersRes.data.filter(o => o.is_active));

        // Fetch nearby and similar restaurants
        try {
          const [nearbyRes, similarRes] = await Promise.all([
            getNearbyRestaurants(id, 500),
            getSimilarCuisineRestaurants(id, 1)
          ]);
          setNearbyRestaurants(nearbyRes.data);
          setSimilarRestaurants(similarRes.data);
        } catch (e) {
          console.log('Could not fetch nearby/similar restaurants');
        }
      } catch (error) {
        console.error('Error fetching restaurant:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const generateGoogleMapsLink = (address) => {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  };

  const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const dayLabels = {
    monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday',
    thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="animate-pulse">
            <div className="h-6 bg-[#F5F5F5] rounded w-24 mb-8" />
            <div className="h-72 bg-[#F5F5F5] rounded-xl mb-6" />
            <div className="h-8 bg-[#F5F5F5] rounded w-3/4 mb-3" />
            <div className="h-5 bg-[#F5F5F5] rounded w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="min-h-screen bg-white py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <h1 className="text-xl font-bold text-[#242E30] mb-4">Restaurant Not Found</h1>
          <Link to="/restaurants" className="text-[#7B2CBF] hover:underline">Back to Restaurants</Link>
        </div>
      </div>
    );
  }

  const imageUrl = restaurant.image_url || defaultImages[0];
  const openingTimes = restaurant.opening_times || {};

  return (
    <div className="min-h-screen bg-white" data-testid="restaurant-detail-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Back Link */}
        <Link 
          to="/restaurants" 
          className="inline-flex items-center gap-2 text-[#595959] hover:text-[#7B2CBF] transition-colors mb-6 text-sm font-medium"
          data-testid="back-to-restaurants"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to restaurants
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Hero Image */}
            <div className="relative h-64 sm:h-80 rounded-xl overflow-hidden mb-6">
              <img src={imageUrl} alt={restaurant.name} className="w-full h-full object-cover" />
              {restaurant.avg_rating > 0 && (
                <div className="absolute top-4 right-4 bg-white text-[#242E30] flex items-center gap-1 px-3 py-2 rounded-lg font-semibold shadow-sm">
                  <Star className="h-4 w-4 fill-[#FFB800] text-[#FFB800]" />
                  {restaurant.avg_rating.toFixed(1)}
                  <span className="text-[#595959] text-sm ml-1">({restaurant.rating_count})</span>
                </div>
              )}
            </div>

            {/* Title & Description */}
            <div className="flex items-start gap-4 mb-4">
              {restaurant.logo_path && (
                <img 
                  src={`${API_URL}/api/files/${restaurant.logo_path}`} 
                  alt={restaurant.name}
                  className="w-16 h-16 object-contain rounded-lg bg-[#F5F5F5] p-2 shrink-0"
                />
              )}
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-[#242E30]">{restaurant.name}</h1>
                <div className="flex items-center gap-2 flex-wrap mt-1">
                  {restaurant.cuisine_type && (
                    <p className="text-[#7B2CBF] font-medium">{restaurant.cuisine_type}</p>
                  )}
                  {restaurant.hygiene_rating ? <HygieneBadge rating={restaurant.hygiene_rating} /> : null}
                </div>
              </div>
            </div>

            <p className="text-[#595959] mb-6">{restaurant.description}</p>

            {/* Current Offers */}
            {offers.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-bold text-[#242E30] mb-4">Current Deals</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {offers.map((offer, index) => (
                    <DealCard key={offer.id} offer={{...offer, restaurant_name: restaurant.name, cuisine_type: restaurant.cuisine_type}} index={index} />
                  ))}
                </div>
              </div>
            )}

            {/* Nearby Restaurants */}
            {nearbyRestaurants.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-bold text-[#242E30] mb-4">
                  Restaurants within 500 yards
                </h2>
                <div className="space-y-3">
                  {nearbyRestaurants.map((r) => (
                    <Link
                      key={r.id}
                      to={`/restaurant/${r.id}`}
                      className="flex items-center gap-4 p-4 bg-[#F5F5F5] rounded-xl hover:bg-[#E8E8E8] transition-colors"
                    >
                      <img 
                        src={r.image_url || defaultImages[0]} 
                        alt={r.name}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <h3 className="font-semibold text-[#242E30]">{r.name}</h3>
                        {r.cuisine_type && <p className="text-sm text-[#7B2CBF]">{r.cuisine_type}</p>}
                        {r.distance_yards && (
                          <p className="text-xs text-[#595959]">{r.distance_yards} yards away</p>
                        )}
                      </div>
                      <ChevronRight className="h-5 w-5 text-[#595959]" />
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Similar Cuisine Restaurants */}
            {similarRestaurants.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-bold text-[#242E30] mb-4">
                  Other {restaurant.cuisine_type} restaurants within 1 mile
                </h2>
                <div className="space-y-3">
                  {similarRestaurants.map((r) => (
                    <Link
                      key={r.id}
                      to={`/restaurant/${r.id}`}
                      className="flex items-center gap-4 p-4 bg-[#F5F5F5] rounded-xl hover:bg-[#E8E8E8] transition-colors"
                    >
                      <img 
                        src={r.image_url || defaultImages[0]} 
                        alt={r.name}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <h3 className="font-semibold text-[#242E30]">{r.name}</h3>
                        {r.cuisine_type && <p className="text-sm text-[#7B2CBF]">{r.cuisine_type}</p>}
                        {r.distance_miles && (
                          <p className="text-xs text-[#595959]">{r.distance_miles} miles away</p>
                        )}
                      </div>
                      <ChevronRight className="h-5 w-5 text-[#595959]" />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Restaurant Info */}
          <div className="lg:col-span-1">
            <div className="bg-[#F5F5F5] rounded-xl p-5 sticky top-24">
              <h3 className="font-bold text-[#242E30] text-lg mb-4">Contact & Location</h3>

              <div className="space-y-3">
                {/* Address with Google Maps link */}
                {restaurant.address && (
                  <a 
                    href={generateGoogleMapsLink(restaurant.address)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-2 text-[#595959] hover:text-[#7B2CBF] transition-colors group"
                  >
                    <MapPin className="h-4 w-4 text-[#7B2CBF] shrink-0 mt-0.5" />
                    <span className="text-sm group-hover:underline">{restaurant.address}</span>
                  </a>
                )}

                {/* Phone with tel link */}
                {restaurant.phone && (
                  <a 
                    href={`tel:${restaurant.phone}`}
                    className="flex items-center gap-2 text-[#595959] hover:text-[#7B2CBF] transition-colors"
                  >
                    <Phone className="h-4 w-4 text-[#7B2CBF] shrink-0" />
                    <span className="text-sm hover:underline">{restaurant.phone}</span>
                  </a>
                )}

                {/* Website */}
                {restaurant.website_url && (
                  <a 
                    href={restaurant.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-[#595959] hover:text-[#7B2CBF] transition-colors"
                  >
                    <Globe className="h-4 w-4 text-[#7B2CBF] shrink-0" />
                    <span className="text-sm hover:underline">Visit Website</span>
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}

                {/* Booking */}
                {restaurant.booking_url && (
                  <a 
                    href={restaurant.booking_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-[#7B2CBF] text-white hover:bg-[#5A189A] rounded-lg px-4 py-2.5 font-medium transition-colors mt-4"
                  >
                    <Calendar className="h-4 w-4" />
                    Book a Table
                  </a>
                )}
              </div>

              {/* Opening Times */}
              {Object.keys(openingTimes).length > 0 && (
                <div className="mt-6 pt-4 border-t border-[#E8E8E8]">
                  <h4 className="font-semibold text-[#242E30] text-sm mb-3 flex items-center gap-2">
                    <Clock className="h-4 w-4 text-[#7B2CBF]" />
                    Opening Times
                  </h4>
                  <div className="space-y-1">
                    {dayOrder.map((day) => (
                      openingTimes[day] && (
                        <div key={day} className="flex justify-between text-sm">
                          <span className="text-[#595959]">{dayLabels[day]}</span>
                          <span className="text-[#242E30] font-medium">{openingTimes[day]}</span>
                        </div>
                      )
                    ))}
                  </div>
                </div>
              )}

              {/* Google ratings & opening hours (auto-loaded, cached 12h) */}
              <div className="mt-6 pt-4 border-t border-[#E8E8E8]">
                {loadingGoogle && (
                  <div className="flex items-center gap-2 text-sm text-[#595959]" data-testid="google-info-loading">
                    <Loader2 className="h-4 w-4 animate-spin text-[#7B2CBF]" />
                    Loading ratings & hours…
                  </div>
                )}
                {googleInfo && googleInfo.available && (
                  <div data-testid="google-info">
                    {googleInfo.rating != null && (
                      <div className="flex items-center gap-2 mb-3">
                        <div className="flex items-center gap-1 bg-white px-2.5 py-1 rounded-lg border border-[#E8E8E8]">
                          <Star className="h-4 w-4 fill-[#FFB800] text-[#FFB800]" />
                          <span className="font-semibold text-[#242E30]">{Number(googleInfo.rating).toFixed(1)}</span>
                        </div>
                        <span className="text-sm text-[#595959]">
                          {(googleInfo.user_rating_count || 0).toLocaleString()} Google reviews
                        </span>
                      </div>
                    )}
                    {googleInfo.opening_hours && googleInfo.opening_hours.length > 0 ? (
                      <div>
                        <h4 className="font-semibold text-[#242E30] text-sm mb-2 flex items-center gap-2">
                          <Clock className="h-4 w-4 text-[#7B2CBF]" />
                          Opening Hours
                        </h4>
                        <ul className="space-y-1">
                          {googleInfo.opening_hours.map((line, i) => (
                            <li key={i} className="text-sm text-[#595959]">{line}</li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <p className="text-sm text-[#595959]">Opening hours not available.</p>
                    )}
                    <p className="text-[11px] text-[#8A8A8A] mt-3">{googleInfo.attribution || 'Data from Google'}</p>
                  </div>
                )}
                {googleInfo && !googleInfo.available && (
                  <p className="text-sm text-[#595959]" data-testid="google-info-unavailable">
                    No Google info found for this venue.
                  </p>
                )}
              </div>

              {/* Claim this business */}
              {(restaurant.is_scraped || !restaurant.owner_id) && (
                <Link
                  to={`/register?role=restaurant&claim=${restaurant.id}`}
                  className="mt-4 w-full flex items-center justify-center gap-2 bg-[#F5F5F5] hover:bg-[#E8E8E8] text-[#242E30] rounded-lg px-4 py-2.5 font-medium transition-colors border border-[#E8E8E8]"
                  data-testid="claim-business-link"
                >
                  <Store className="h-4 w-4 text-[#7B2CBF]" />
                  Claim this business
                </Link>
              )}

              {/* Menu PDF Download */}
              {restaurant.menu_pdf_path && (
                <a 
                  href={`${API_URL}/api/files/${restaurant.menu_pdf_path}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-white border border-[#E8E8E8] text-[#242E30] hover:border-[#7B2CBF] hover:text-[#7B2CBF] rounded-lg px-4 py-2.5 font-medium transition-colors mt-4"
                >
                  <FileText className="h-4 w-4" />
                  Download Menu (PDF)
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestaurantDetailPage;
